import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, User, KeyRound, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { roleFor } from "@/lib/roles";
import { RoleBadge } from "@/components/RoleBadge";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { user, logout, site, roles } = useAuth();
  const { setOpen, requestCount } = useChat();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const role = roleFor(user, roles);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#05070D]/85 border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-brand-logo" className="flex items-center gap-3 group">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center font-display font-black text-[#05070D] shadow-[0_0_24px_-6px_rgba(0,240,255,0.7)]">C</div>
          <span className="font-display font-bold tracking-tight text-slate-50 text-sm sm:text-base group-hover:text-cyan-300 transition-colors">
            {site?.site_title || "COREXE INFO PUGLIC"}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-2 text-xs text-slate-400 font-mono-cx" data-testid="system-status-badge">
          <span className="cx-live-dot" /> Sistem Aktif
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="public-chat-toggle"
            onClick={() => setOpen(true)}
            className="relative h-10 px-3 sm:px-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-cyan-500/50 text-slate-200 text-sm font-semibold inline-flex items-center gap-2 transition-colors"
          >
            <MessageSquare className="h-4 w-4 text-cyan-400" />
            <span className="hidden sm:inline">Sohbet</span>
            {requestCount > 0 && (
              <span data-testid="dm-request-badge" className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {requestCount}
              </span>
            )}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-profile-dropdown" className="h-10 pl-1.5 pr-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-cyan-500/50 inline-flex items-center gap-2 transition-colors">
                <div className="h-7 w-7 rounded-lg bg-slate-800 flex items-center justify-center"><User className="h-4 w-4 text-cyan-300" /></div>
                <span className="text-sm font-semibold text-slate-100 max-w-[110px] truncate">{user?.username}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100">
              <DropdownMenuLabel className="space-y-1.5">
                <p className="text-xs text-slate-500 font-mono-cx uppercase tracking-widest">Profil</p>
                <p className="font-semibold">{user?.username}</p>
                {role ? <RoleBadge role={role} /> : <span className="text-[11px] text-slate-500">Üye</span>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem data-testid="change-password-button" onClick={() => setPwOpen(true)} className="gap-2 focus:bg-slate-800 focus:text-cyan-300 cursor-pointer">
                <KeyRound className="h-4 w-4" /> Şifre Değiştir
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem data-testid="go-admin-panel" onClick={() => navigate("/admin")} className="gap-2 focus:bg-slate-800 focus:text-red-300 cursor-pointer">
                  <LayoutDashboard className="h-4 w-4" /> Admin Paneli
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="gap-2 focus:bg-slate-800 focus:text-red-300 cursor-pointer">
                <LogOut className="h-4 w-4" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}
